import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from './Sidebar';
import { useShop } from '../context/ShopContext';

const PrintSlip = () => {
  const [stockDate, setStockDate] = useState('');
  const [loading, setLoading] = useState(false);
  const { shopName, shopId, companyName } = useShop();

  useEffect(() => {
    setStockDate(new Date().toISOString().split('T')[0]); // default to today
  }, []);

  const drawHeader = (doc, { shopName, licenseNo, slipName, date }) => {
    let formattedDate = date;
    if (date.includes('-')) {
      const [day, month ,year] = date.split('-');
      formattedDate = `${Number(day)}-${Number(month)}-${year}`;
    }
    autoTable(doc, {
      margin: { top: 14, left: 14, right: 14 }, // tight margins
      startY: 14, // exact top position
      head: [[
        { content: `Shop Name: ${shopName || 'N/A'}`, styles: { halign: 'left' } },
        { content: `Purcha: ${slipName}`, styles: { halign: 'center' } },
        { content: `SalesmanName: ${licenseNo || 'N/A'}`, styles: { halign: 'center' } },
      
        { content: `Date: ${formattedDate}`, styles: { halign: 'right' } }
      ]],
      styles: {
        fontSize: 10,
        cellPadding: 2, // reduce vertical space
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      tableLineWidth: 0.5,
      tableLineColor: [0, 0, 0]
    });
  };



  const renderTable = (doc, data, startY, slipName) => {
    const tableStartY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 5 : startY;
    if (data.length) {
      autoTable(doc, {
        head: [
          ['S.No.', 'Product Name', 'Variant', 'Opening', 'Received', 'Transferred', 'Total', 'Closing', 'Sales', 'Rate', 'Amount']
        ],
        body: data.map((i, index) => [
          index + 1,
          i.pName,
          i.packing,
          i.openingStock,
          i.rece,
          i.trans,
          (i.openingStock + i.rece - i.trans),
          i.closingStock,
          i.sales,
          i.mrp,
          i.amount,
        ]),
        startY: tableStartY,
        styles: { fontSize: 5, cellPadding: 1, halign: 'center', lineColor: '#000', lineWidth: 0.5 },
        headStyles: { fillColor: [0, 123, 255], textColor: '#fff', fontStyle: 'bold' },
      
      });
    } else {
      doc.setFontSize(12).text('No records found.', 14, tableStartY);
    }
  };

// ... imports and component definition remain the same

const calculateTotals = (data, keys) => {
  const totals = keys.map(key => data.reduce((sum, row) => sum + Number(row[key] || 0), 0));
  return totals;
};

const generatePDF = async () => {
  setLoading(true);
  try {
    const { data: resp } = await axios.post(
      'https://winenew.sipstocks.in/api/Liquor/GetPrintSlipData',
      { stockDate: new Date(stockDate).toISOString(), shopId },
      { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
    );

    const english = resp.data?.productEnglishSummaries || [];
    const desi = resp.data?.productDesiSummaries || [];
    const sales = resp.data?.productSalesResponses || [];

    const doc = new jsPDF();
    const dateObj = new Date(stockDate);
    const formattedDate = `${dateObj.getDate()}-${dateObj.getMonth() + 1}-${dateObj.getFullYear()}`;
    const margin = 14;
    const startY = 30;

    const renderTableWithFooter = (doc, { title, data, columns, totalKeys, startY, shopName, licenseNo, date }) => {
      drawHeader(doc, {
        shopName,
        slipName: title,
        licenseNo,
        date
      });

      const dataTableStartY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 0.5 : startY;

      if (data.length) {
        const footTotals = calculateTotals(data, totalKeys);
        const footRow = [
          { content: 'Total', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
          ...footTotals.map(val => ({ content: val.toFixed(2), styles: { fontStyle: 'bold' } }))
        ];

        autoTable(doc, {
          head: [columns],
          body: data.map((i, index) => [
            index + 1,
            i.pName,
            i.packing,
            i.openingStock,
            i.rece,
            i.trans,
            (i.openingStock + i.rece - i.trans),
            i.closingStock,
            i.sales,
            i.mrp,
            i.amount,
            i.rdDebit,
            i.rdCredit,
          ]),
          foot: [footRow],
          showFoot: 'lastPage',
          startY: dataTableStartY,
          styles: {
            fontSize: 5,
            cellPadding: 1,
            halign: 'center',
            lineColor: '#000',
            lineWidth: 0.5
          },
          headStyles: {
            fillColor: [0, 80, 255],
            textColor: '#fff',
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontStyle: 'bold',
            textColor: '#000'
          },
          footStyles: {
            fillColor: [230, 230, 230],
            textColor: '#000',
            fontStyle: 'bold'
          }
        });
      } else {
        doc.setFontSize(12).text('No records found.', margin, dataTableStartY);
      }
    };

    const renderSalesTable = (doc, data, startY) => {
      drawHeader(doc, {
        shopName,
        slipName: 'Net Sale',
        licenseNo: companyName,
        date: formattedDate
      });

      const dataTableStartY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 0.5 : startY;

      if (data.length) {
        const footTotals = calculateTotals(data, ['english', 'desi', 'sales', 'exp', 'debit', 'credit', 'salary', 'commissionValue', 'amount', 'rdDebit', 'rdCredit']);
        const footRow = [
          { content: 'Total', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
          ...footTotals.map(val => ({ content: val.toFixed(2), styles: { fontStyle: 'bold' } }))
        ];

        autoTable(doc, {
          head: [['Shop Name', 'Date', 'English', 'Desi', 'Total Sales', 'Total Expense', 'Debit', 'Credit', 'Salary', 'Commission', 'Total Net Sales', 'RD Debit', 'RD Credit']],
          body: data.map(i => [
            i.shopName,
            i.salesDate?.split('T')[0]?.split('-').reverse().join('-') || '',
            i.english,
            i.desi,
            i.sales,
            i.exp,
            i.debit,
            i.credit,
            i.salary,
            i.commissionValue,
            i.amount,
            i.rdDebit,
            i.rdCredit,
          ]),
          foot: [footRow],
          showFoot: 'lastPage',
          startY: dataTableStartY,
          styles: {
            fontSize: 5,
            cellPadding: 1,
            halign: 'center',
            lineColor: '#000',
            lineWidth: 0.5
          },
          headStyles: {
            fillColor: [0, 123, 255],
            textColor: '#fff',
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontStyle: 'bold',
            textColor: '#000'
          },
          footStyles: {
            fillColor: [230, 230, 230],
            textColor: '#000',
            fontStyle: 'bold'
          }
        });
      } else {
        doc.setFontSize(12).text('No records found.', margin, dataTableStartY);
      }
    };

    const productColumns = ['S.No.', 'Product Name', 'Variant', 'Opening Stock', 'Received', 'Transferred', 'Total', 'Closing Stock', 'Sales', 'Rate', 'Amount', 'RD Debit', 'RD Credit'];
    const productTotalKeys = ['openingStock', 'rece', 'trans', 'Total', 'closingStock', 'sales', 'mrp', 'amount', 'rdDebit', 'rdCredit'];

    renderTableWithFooter(doc, {
      title: 'English Record',
      data: english,
      columns: productColumns,
      totalKeys: productTotalKeys,
      startY,
      shopName,
      licenseNo: companyName,
      date: formattedDate
    });

    doc.addPage();
    renderTableWithFooter(doc, {
      title: 'Desi Record',
      data: desi,
      columns: productColumns,
      totalKeys: productTotalKeys,
      startY,
      shopName,
      licenseNo: companyName,
      date: formattedDate
    });

    doc.addPage();
    renderSalesTable(doc, sales, startY);

    const dateStr = new Date(stockDate).toLocaleDateString('en-GB').replace(/\//g, '-');
    doc.save(`Liquor-PrintSlip(${dateStr}).pdf`);

  } catch (err) {
    console.error('PDF Generation Error:', err);
    toast.error('Error generating PDF.');
  } finally {
    setLoading(false);
  }
};




  
  

  const handleSubmit = e => {
    e.preventDefault();
    if (!stockDate) {
      toast.error('Please select a stock date.');
      return;
    }
    generatePDF();
  };

  return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
             {/* Content wrapper */}
             <div className="d-flex flex-column flex-column-fluid">
                 {/* Toolbar */}
                 <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                     {/* Toolbar container */}
                     <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                         {/* Page title */}
                         <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                             {/* Title */}
                             <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center my-0">
                             Liquor Print Slip PDF
                             </h1>
                             <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                                 <li className="breadcrumb-item text-muted">
                                     <a href="index.html" className="text-muted text-hover-primary">Home</a>
                                 </li>
                                 <li className="breadcrumb-item">
                                     <span className="bullet bg-gray-500 w-5px h-2px"></span>
                                 </li>
                                 <li className="breadcrumb-item text-muted">Dashboards</li>
                             </ul>
                         </div>
                     </div>
                 </div>
           
                 <div id="kt_app_content" className="app-content flex-column-fluid">
                     <div id="kt_app_content_container" className="app-container container-fluid">
                         <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
                             <div className="col-xl-12">
                                 <div className="card card-flush h-md-100">
                                      <ToastContainer />
                                   
                                     <div className="card-body pt-6">

                                   
                      <form onSubmit={handleSubmit} style={{ marginBottom: '20px' }}>
                        <div className="form-row">
                          <div className="form-group col-md-3">
                            <label>Stock Date:</label>
                            <input
                              type="date"
                              className="form-control"
                              value={stockDate}
                              onChange={(e) => setStockDate(e.target.value)}
                            />
                          </div>

                          <div className="form-group col-md-3">
                            <label>&nbsp;</label>
                            <button
                              type="submit"
                              className="btn btn-primary form-control"
                              disabled={loading}
                            >
                              {loading ? "Generating..." : "Download PDF"}
                            </button>
                          </div>
                        </div>
                      </form>

                      <div className="clearfix"></div>
                
                                 </div>
                             </div>
                         </div>
                     </div>
                 </div>
             </div>
           
             {/* Footer */}
             <div id="kt_app_footer" className="app-footer">
                 <div className="app-container container-fluid d-flex flex-column flex-md-row flex-center flex-md-stack py-3">
                     <div className="text-gray-900 order-2 order-md-1">
                         <span className="text-muted fw-semibold me-1">2025&copy;</span>
                         <a href="#" target="_blank" className="text-gray-800 text-hover-primary">WineShop</a>
                     </div>
                 </div>
             </div>
         
           
           
           
           
           
           
           
       
           
          
           
           
           
           
           
          
           
         
           </div>
           </div>
  );
};

export default PrintSlip;
